import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

export interface Belegtext {
  belegtext_id: number;
  belegart: string;
  position: string;
  text: string;
  aktiv: boolean;
}

interface Props {
  onFehler: (m: string | null) => void;
}

const BELEGARTEN: Array<[string, string]> = [
  ['ANGEBOT', 'Angebot'],
  ['AUFTRAGSBESTAETIGUNG', 'Auftragsbestätigung'],
  ['RECHNUNG', 'Rechnung'],
  ['LIEFERSCHEIN', 'Lieferschein'],
  ['BESTELLUNG', 'Bestellung'],
  ['GUTSCHRIFT', 'Gutschrift'],
  ['MAHNUNG', 'Mahnung'],
];

const POSITIONEN: Array<[string, string]> = [
  ['KOPF', 'Kopftext'],
  ['FUSS', 'Fußtext'],
];

/** Draft-Schlüssel für `entwuerfe`, aus Belegart + Position. */
const entwurfSchluessel = (belegart: string, position: string) => `${belegart}-${position}`;

/**
 * Belegtexte-Reiter der Firma-Sektion (`/api/firma/belegtexte`, Task 4-API).
 * Speichert je Belegart/Position beim Verlassen des Feldes (onBlur) statt
 * über einen Sammel-Button — bei 7 Belegarten x 2 Positionen wären 14
 * Einzel-Buttons unübersichtlicher als ein Blur-Save je Feld.
 *
 * Die Textareas sind controlled Inputs über `entwuerfe` (lokaler Draft-State
 * je Belegart+Position) — NICHT `defaultValue` + remount-`key` wie in einer
 * früheren Fassung. Ablauf des Datenverlust-Bugs, den das ersetzt: Nutzer
 * verlässt das Feld (Autosave feuert), fokussiert es sofort wieder und tippt
 * weiter; die Hintergrund-Antwort des Speicherns trifft ein, der `key`
 * ändert sich dadurch, React hängt die Textarea aus und wieder ein — die
 * Eingabe ist weg, ohne jede Warnung. Mit einem controlled Draft, der nie
 * durch eintreffende Serverdaten überschrieben wird, kann das nicht mehr
 * passieren: ein Draft wird nur gesetzt, wenn für den Schlüssel noch keiner
 * existiert, nie erneut. Aus demselben Grund aktualisiert ein erfolgreiches
 * Speichern `texte` lokal aus der Serverantwort statt per erneutem `load()`
 * (GET) — ein GET würde genau das Zeitfenster wieder öffnen, das dieser Fix
 * schließen soll.
 *
 * `aktiv` wird beim Speichern immer explizit aus dem geladenen Datensatz
 * mitgeschickt (Default: true für neue Texte): die API setzt `aktiv` beim
 * Fehlen im Body auf true zurück, ein Blur-Save ohne dieses Feld würde also
 * einen anderweitig deaktivierten Text stillschweigend reaktivieren.
 */
const FirmaBelegtexte: React.FC<Props> = ({ onFehler }) => {
  const [texte, setTexte] = useState<Belegtext[]>([]);
  const [loading, setLoading] = useState(true);
  const [belegart, setBelegart] = useState(BELEGARTEN[0][0]);
  const [gespeichertFeld, setGespeichertFeld] = useState<string | null>(null);
  const [entwuerfe, setEntwuerfe] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    setLoading(true);
    axios.get<Belegtext[]>(`${API_URL}/api/firma/belegtexte`)
      .then(r => setTexte(r.data))
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
        onFehler(`Belegtexte konnten nicht geladen werden: ${msg}`);
      })
      .finally(() => setLoading(false));
  }, [onFehler]);

  useEffect(() => { load(); }, [load]);

  const eintrag = (position: string) => texte.find(t => t.belegart === belegart && t.position === position);
  const aktuellerText = (position: string) => eintrag(position)?.text ?? '';

  // Draft je Belegart+Position seeden, sobald der erste Serverstand vorliegt
  // (deshalb die loading-Bremse: vor der GET-Antwort wäre `texte` noch leer,
  // ein Seed daraus würde als leerer Draft "einrasten" und nie mehr durch
  // die echten Texte ersetzt, weil ein bestehender Draft absichtlich nie
  // überschrieben wird — s.o.). Läuft auch bei jedem Belegart-Wechsel, seedet
  // dann aber nur die beiden noch nie besuchten Schlüssel der neuen Belegart.
  useEffect(() => {
    if (loading) return;
    setEntwuerfe(prev => {
      let next = prev;
      for (const [position] of POSITIONEN) {
        const k = entwurfSchluessel(belegart, position);
        if (!(k in next)) {
          if (next === prev) next = { ...prev };
          next[k] = texte.find(t => t.belegart === belegart && t.position === position)?.text ?? '';
        }
      }
      return next;
    });
  }, [texte, belegart, loading]);

  const speichern = async (position: string) => {
    const k = entwurfSchluessel(belegart, position);
    const text = entwuerfe[k] ?? '';
    if (text === aktuellerText(position)) return; // unveraendert -> kein Request
    onFehler(null);
    const aktiv = eintrag(position)?.aktiv ?? true;
    // Schlüssel aus Belegart + Position: sonst würde ein Wechsel der Belegart
    // innerhalb der 2s-Anzeigedauer den Haken fälschlich neben dem Feld der
    // neu gewählten Belegart aufblitzen lassen.
    const feldSchluessel = `${belegart}:${position}`;
    try {
      const r = await axios.put<Belegtext>(`${API_URL}/api/firma/belegtexte`, { belegart, position, text, aktiv });
      // Serverantwort trägt den gespeicherten Datensatz - lokal mergen statt
      // load(): ein erneutes GET während der Nutzer weitertippt könnte seine
      // Eingabe überschreiben (siehe Komponenten-Doku oben).
      setTexte(prev => [
        ...prev.filter(t => !(t.belegart === r.data.belegart && t.position === r.data.position)),
        r.data,
      ]);
      setGespeichertFeld(feldSchluessel);
      window.setTimeout(() => setGespeichertFeld(f => (f === feldSchluessel ? null : f)), 2000);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
      onFehler(`Belegtext konnte nicht gespeichert werden: ${msg}`);
    }
  };

  return (
    <div>
      <p style={{ color: 'var(--ink-2)', marginBottom: 16, fontSize: 14 }}>
        Diese Texte sind Vorgaben je Belegart. Ein am einzelnen Beleg gepflegter Kopf- oder
        Fußtext hat beim Rendern Vorrang vor dem hier hinterlegten Standardtext. Gespeichert
        wird automatisch beim Verlassen eines Feldes.
      </p>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 20, maxWidth: 280 }}>
        Belegart
        <select value={belegart} onChange={e => setBelegart(e.target.value)} style={{ padding: '7px 10px' }}>
          {BELEGARTEN.map(([wert, label]) => <option key={wert} value={wert}>{label}</option>)}
        </select>
      </label>

      {loading ? (
        <div>Lade Belegtexte…</div>
      ) : (
        POSITIONEN.map(([position, label]) => {
          const k = entwurfSchluessel(belegart, position);
          return (
            <label key={k} style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                {label}
                {gespeichertFeld === `${belegart}:${position}` && (
                  <span style={{ color: 'var(--green-500)', fontSize: 12 }}>✓ Gespeichert</span>
                )}
              </span>
              <textarea
                rows={4}
                value={entwuerfe[k] ?? ''}
                onChange={e => {
                  const wert = e.target.value;
                  setEntwuerfe(prev => ({ ...prev, [k]: wert }));
                }}
                onBlur={() => speichern(position)}
                style={{ padding: '8px 10px', width: '100%', boxSizing: 'border-box' }}
              />
            </label>
          );
        })
      )}
    </div>
  );
};

export default FirmaBelegtexte;
